import { Link } from "react-router-dom";
import { Briefcase, MapPin, Clock, ArrowRight, Mail } from "lucide-react";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";
import { COMPANY } from "@/lib/company";

const GOLD = "#C9A84C";

const ROLES = [
  ["Planning Engineer — Product", "Ghazipur / Remote", "Full time",
   "You have run L2/L3 schedules on a live EPC package and want to turn that judgement into software behaviour. You will shape how the CPM, EVM and forecasting engines think."],
  ["Full-stack Engineer (React / FastAPI)", "Remote, India", "Full time",
   "You will build modules that site engineers use at 6 AM on a 2G connection. Offline-first, fast, and boringly reliable."],
  ["Implementation & Customer Success", "Ghazipur / Travel", "Full time",
   "You will set up first project packages — WBS coding, BOQ import, baselines, roles — and train planning teams so the DPR discipline sticks."],
];

export default function Careers() {
  useMeta({
    title: "Careers at Plan-Sarthi | Build EPC Project Controls Software",
    description:
      "Join Plan-Sarthi, the Indian EPC project controls platform by Sarthi Associates. Open roles in planning, engineering and customer success. Write to admin@plansarthi.com.",
    path: "/careers",
  });

  return (
    <div className="min-h-screen bg-[#0A1628] text-white flex flex-col" data-testid="careers-page">
      <PublicHeader />
      <main className="flex-1 max-w-[1000px] mx-auto px-5 sm:px-8 py-14 w-full">
        <span className="text-[11px] uppercase tracking-[0.18em]" style={{ color: GOLD }}>Careers</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight mt-4" data-testid="page-title">
          Build the platform India's project engineers actually trust
        </h1>
        <p className="text-base text-white/70 mt-5 leading-relaxed max-w-[720px]">
          We are a small team at {COMPANY.legalName} building Plan-Sarthi for the people who deliver the country's
          power, refinery and infrastructure projects. If you have stood on a site at 6 AM chasing a pour, or you write
          software that has to work on a 2G connection, you will fit here.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mt-10">
          {[["Real users", "Your work is used on live packages worth hundreds of crores, not a demo."],
            ["Small team", "No layers. You own a module end to end and see it on site within weeks."],
            ["Domain depth", "You will learn EPC project controls properly — WBS, EVM, CPM, claims."]].map(([t, d]) => (
            <div key={t} className="border border-white/10 bg-[#112240] rounded-lg p-5">
              <h3 className="font-semibold text-[14px]" style={{ color: GOLD }}>{t}</h3>
              <p className="text-[12.5px] text-white/60 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <h2 className="text-base md:text-lg font-semibold uppercase tracking-[0.14em] mt-14" style={{ color: GOLD }}>
          Open roles
        </h2>
        <div className="space-y-3 mt-6" data-testid="careers-roles">
          {ROLES.map(([title, place, type, text], i) => (
            <div key={title} className="border border-white/10 bg-[#112240] rounded-lg p-5" data-testid={`career-role-${i}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <h3 className="font-semibold text-[15px] flex items-center gap-2">
                  <Briefcase className="w-4 h-4" style={{ color: GOLD }} /> {title}
                </h3>
                <span className="text-[11.5px] text-white/55 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {place}</span>
                <span className="text-[11.5px] text-white/55 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {type}</span>
              </div>
              <p className="text-[13px] text-white/65 mt-3 leading-relaxed">{text}</p>
              <a
                href={`mailto:${COMPANY.email}?subject=${encodeURIComponent(`Application — ${title}`)}`}
                className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-medium"
                style={{ color: GOLD }}
                data-testid={`career-apply-${i}`}
              >
                Apply by email <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          ))}
        </div>

        <div className="border rounded-lg p-6 mt-10" style={{ borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.07)" }} data-testid="careers-cta">
          <h2 className="text-base md:text-lg font-semibold">Nothing that fits, but you still want in?</h2>
          <p className="text-[13px] text-white/65 mt-2 leading-relaxed">
            Write to us with what you have built or delivered. We read every email and we hire outside a job
            description when someone is clearly right.
          </p>
          <a href={`mailto:${COMPANY.email}?subject=Speculative%20application`}
             className="inline-flex items-center gap-2 mt-5 h-10 px-5 rounded-lg text-[14px] font-semibold text-white"
             style={{ backgroundColor: GOLD }} data-testid="careers-email-btn">
            <Mail className="w-4 h-4" /> {COMPANY.email}
          </a>
        </div>

        <p className="text-[12.5px] text-white/50 mt-8">
          Curious what you would be working on? Read the <Link to="/blog" className="hover:underline" style={{ color: GOLD }}>blog</Link>{" "}
          or explore the <Link to="/features" className="hover:underline" style={{ color: GOLD }}>platform</Link>.
        </p>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
